/**
 * KitsManager (iter-95b) — full-fledged list of Publisher's Kits.
 *
 * Rendered on the Preset Templates page. Shows every kit the caller
 * can see (own + shared by teammates), with expand-to-inspect,
 * rename, edit (full modal) and delete.
 *
 * Ownership rules:
 *   • `k.is_mine` → rename / edit / delete enabled
 *   • Read-only kits → info-only view
 */
import React, { useMemo, useState } from "react";
import { toast } from "sonner";
import { ChevronDown, ChevronRight, Pencil, Trash2, Check, X, Edit3, Users, Share2 } from "lucide-react";
import { CreateKitModal } from "./PublishersKit";
import { api, formatApiError } from "@/lib/api";

function _summary(k) {
  return [
    k.info_preset_id ? "book info" : null,
    (k.front_preset_ids?.length || 0) > 0 ? `${k.front_preset_ids.length} front` : null,
    (k.back_preset_ids?.length || 0) > 0 ? `${k.back_preset_ids.length} back` : null,
    (k.toc_note || "").trim() ? "TOC note" : null,
    (k.introduction || "").trim() ? "intro" : null,
  ].filter(Boolean);
}

function _presetById(presets, id) {
  return presets.find((p) => p.id === id) || null;
}

export function KitsManager({ kits, presets, team, workspaces, onRenameKit, onUpdateKit, onDeleteKit, onKitsChanged }) {
  const [expandedId, setExpandedId] = useState("");
  const [renamingId, setRenamingId] = useState("");
  const [renameDraft, setRenameDraft] = useState("");
  const [editingKit, setEditingKit] = useState(null);

  // iter-102 — bulk workspace share. Author ticks any number of THEIR
  // own kits, then clicks "Share selected…" to open a mini modal
  // (team checkbox + workspace multi-select) that patches every
  // selected kit in one round-trip per kit.
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);

  const ownedKits = useMemo(() => kits.filter((k) => k.is_mine), [kits]);
  const anySelected = selectedIds.size > 0;
  const allOwnedSelected = ownedKits.length > 0 && ownedKits.every((k) => selectedIds.has(k.id));

  const toggleSelect = (kitId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(kitId)) next.delete(kitId); else next.add(kitId);
      return next;
    });
  };
  const selectAllOwned = () => {
    if (allOwnedSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(ownedKits.map((k) => k.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const front = useMemo(() => presets.filter((p) => p.kind === "front"), [presets]);
  const back  = useMemo(() => presets.filter((p) => p.kind === "back"),  [presets]);
  const info  = useMemo(() => presets.filter((p) => p.kind === "info"),  [presets]);

  const startRename = (k) => {
    setRenamingId(k.id);
    setRenameDraft(k.name || "");
  };
  const commitRename = async (k) => {
    const next = (renameDraft || "").trim();
    if (!next || next === k.name) { setRenamingId(""); return; }
    const ok = await onRenameKit(k.id, next);
    if (ok) setRenamingId("");
  };

  return (
    <section className="border border-[color:var(--border)] bg-white p-4 space-y-3"
             data-testid="pt-kits-manager">
      <div>
        <p className="text-xs uppercase tracking-[0.25em] text-[color:var(--text-muted)]">§ Kits</p>
        <p className="text-xs text-[color:var(--text-muted)] mt-1">
          Every Publisher's Kit you can see. Click a kit to inspect its bundled
          front / back matter, book-info fields and TOC / intro copy. Rename or
          delete in place; the pencil opens the full editor.
        </p>
      </div>

      {/* iter-102 — Bulk action bar. Only appears when ≥1 kit is
          selected. Also renders the "Select all my kits" checkbox
          when the author has more than one owned kit. */}
      {ownedKits.length > 1 && (
        <div className="flex items-center gap-2 text-xs text-[color:var(--text-muted)] border-t border-[color:var(--border)] pt-2 -mt-1"
             data-testid="pt-kits-bulk-bar">
          <label className="inline-flex items-center gap-1 cursor-pointer" title="Select all your kits">
            <input type="checkbox" checked={allOwnedSelected}
                   onChange={selectAllOwned}
                   data-testid="pt-kits-select-all"/>
            <span>Select all my kits ({ownedKits.length})</span>
          </label>
          {anySelected && (
            <>
              <span className="mx-1 h-3 w-px bg-[color:var(--border)]" aria-hidden/>
              <span className="text-[color:var(--text)] font-medium" data-testid="pt-kits-bulk-count">
                {selectedIds.size} selected
              </span>
              <button type="button" onClick={()=>setBulkOpen(true)}
                      className="inline-flex items-center gap-1 px-2 py-0.5 border border-[color:var(--primary)] text-[color:var(--primary)] hover:bg-[color:var(--canvas)]"
                      data-testid="pt-kits-bulk-share">
                <Share2 className="w-3 h-3"/> Share selected with…
              </button>
              <button type="button" onClick={clearSelection}
                      className="underline hover:text-[color:var(--text)]"
                      data-testid="pt-kits-bulk-clear">
                Clear
              </button>
            </>
          )}
        </div>
      )}

      {kits.length === 0 ? (
        <p className="text-sm text-[color:var(--text-muted)] italic"
           data-testid="pt-kits-empty">
          No kits yet — create one from the “+ New kit” button above.
        </p>
      ) : (
        <ul className="divide-y divide-[color:var(--border)]"
            data-testid="pt-kits-list">
          {kits.map((k) => {
            const isOpen = expandedId === k.id;
            const isRenaming = renamingId === k.id;
            const bits = _summary(k);
            return (
              <li key={k.id} className="py-3" data-testid={`pt-kit-row-${k.id}`}>
                {/* --- header row --- */}
                <div className="flex items-center gap-2">
                  {/* iter-102 — per-row select checkbox (owner-only). */}
                  {k.is_mine ? (
                    <input type="checkbox"
                           checked={selectedIds.has(k.id)}
                           onChange={()=>toggleSelect(k.id)}
                           className="ml-1"
                           title="Select this kit for bulk share"
                           data-testid={`pt-kit-${k.id}-select`}/>
                  ) : (
                    // spacer so read-only rows align with owner rows
                    <span className="ml-1 w-3.5" aria-hidden/>
                  )}
                  <button type="button"
                    onClick={() => setExpandedId(isOpen ? "" : k.id)}
                    className="p-1 text-[color:var(--text-muted)] hover:text-[color:var(--text)]"
                    aria-expanded={isOpen}
                    data-testid={`pt-kit-${k.id}-toggle`}
                    title={isOpen ? "Collapse" : "Expand to see kit contents"}>
                    {isOpen ? <ChevronDown className="w-4 h-4"/> : <ChevronRight className="w-4 h-4"/>}
                  </button>

                  {isRenaming ? (
                    <div className="flex-1 flex items-center gap-1"
                         data-testid={`pt-kit-${k.id}-rename-form`}>
                      <input
                        autoFocus
                        value={renameDraft}
                        onChange={(e) => setRenameDraft(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") commitRename(k);
                          if (e.key === "Escape") setRenamingId("");
                        }}
                        className="flex-1 px-2 py-1 text-sm border border-[color:var(--border)]"
                        data-testid={`pt-kit-${k.id}-rename-input`}/>
                      <button type="button" onClick={() => commitRename(k)}
                        className="p-1 text-emerald-700 hover:text-emerald-900"
                        title="Save name"
                        data-testid={`pt-kit-${k.id}-rename-save`}>
                        <Check className="w-4 h-4"/>
                      </button>
                      <button type="button" onClick={() => setRenamingId("")}
                        className="p-1 text-[color:var(--text-muted)] hover:text-red-600"
                        title="Cancel"
                        data-testid={`pt-kit-${k.id}-rename-cancel`}>
                        <X className="w-4 h-4"/>
                      </button>
                    </div>
                  ) : (
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-medium text-[color:var(--text)]"
                              data-testid={`pt-kit-${k.id}-name`}>
                          {k.name || "Untitled kit"}
                        </span>
                        {k.is_shared && (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-emerald-100 text-emerald-800 inline-flex items-center gap-1"
                                data-testid={`pt-kit-${k.id}-shared-badge`}>
                            <Users className="w-2.5 h-2.5"/> Shared
                          </span>
                        )}
                        {!k.is_mine && (
                          <span className="text-[9px] uppercase tracking-widest px-1.5 py-0.5 bg-slate-100 text-slate-600">
                            Read-only
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[color:var(--text-muted)] mt-0.5"
                           data-testid={`pt-kit-${k.id}-summary`}>
                        {bits.length ? bits.join(" · ") : "empty"}
                        {k.description ? ` — ${k.description}` : ""}
                      </div>
                    </div>
                  )}

                  {k.is_mine && !isRenaming && (
                    <div className="flex items-center gap-1 shrink-0">
                      <button type="button" onClick={() => startRename(k)}
                        className="p-1.5 text-[color:var(--text-muted)] hover:text-[color:var(--primary)] border border-transparent hover:border-[color:var(--border)]"
                        title="Rename kit"
                        data-testid={`pt-kit-${k.id}-rename`}>
                        <Edit3 className="w-3.5 h-3.5"/>
                      </button>
                      <button type="button" onClick={() => setEditingKit(k)}
                        className="p-1.5 text-[color:var(--text-muted)] hover:text-[color:var(--primary)] border border-transparent hover:border-[color:var(--border)]"
                        title="Open the full kit editor"
                        data-testid={`pt-kit-${k.id}-edit`}>
                        <Pencil className="w-3.5 h-3.5"/>
                      </button>
                      <button type="button" onClick={() => onDeleteKit(k.id)}
                        className="p-1.5 text-[color:var(--text-muted)] hover:text-red-600 border border-transparent hover:border-red-300"
                        title="Delete kit"
                        data-testid={`pt-kit-${k.id}-delete`}>
                        <Trash2 className="w-3.5 h-3.5"/>
                      </button>
                    </div>
                  )}
                </div>

                {/* --- expanded body --- */}
                {isOpen && (
                  <div className="mt-3 ml-6 pl-3 border-l border-[color:var(--border)] space-y-3 text-xs"
                       data-testid={`pt-kit-${k.id}-details`}>
                    {/* Book info */}
                    {k.info_preset_id && (() => {
                      const p = _presetById(info, k.info_preset_id);
                      return (
                        <div>
                          <p className="uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">Book info preset</p>
                          {p ? (
                            <div className="mt-1">
                              <p className="font-medium">{p.name}</p>
                              <ul className="mt-1 space-y-0.5 text-[color:var(--text-muted)]">
                                {Object.entries(p.fields || {}).filter(([, v]) => (v || "").trim()).map(([kk, v]) => (
                                  <li key={kk}><strong className="text-[color:var(--text)]">{kk}:</strong> {String(v)}</li>
                                ))}
                              </ul>
                            </div>
                          ) : (
                            <p className="italic text-[color:var(--text-muted)]">missing (preset deleted?)</p>
                          )}
                        </div>
                      );
                    })()}

                    {/* Front matter */}
                    {(k.front_preset_ids || []).length > 0 && (
                      <div>
                        <p className="uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">Front matter</p>
                        <ul className="mt-1 space-y-1">
                          {k.front_preset_ids.map((id) => {
                            const p = _presetById(front, id);
                            return (
                              <li key={id} className="border border-[color:var(--border)]/60 p-2 bg-[color:var(--canvas)]/50">
                                {p ? (
                                  <>
                                    <p className="font-medium">{p.section_heading || p.name}</p>
                                    <p className="whitespace-pre-wrap text-[color:var(--text-muted)] mt-0.5">{p.content || "(empty)"}</p>
                                  </>
                                ) : (
                                  <p className="italic text-[color:var(--text-muted)]">missing preset ({id})</p>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* Back matter */}
                    {(k.back_preset_ids || []).length > 0 && (
                      <div>
                        <p className="uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">Back matter</p>
                        <ul className="mt-1 space-y-1">
                          {k.back_preset_ids.map((id) => {
                            const p = _presetById(back, id);
                            return (
                              <li key={id} className="border border-[color:var(--border)]/60 p-2 bg-[color:var(--canvas)]/50">
                                {p ? (
                                  <>
                                    <p className="font-medium">{p.section_heading || p.name}</p>
                                    <p className="whitespace-pre-wrap text-[color:var(--text-muted)] mt-0.5">{p.content || "(empty)"}</p>
                                  </>
                                ) : (
                                  <p className="italic text-[color:var(--text-muted)]">missing preset ({id})</p>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}

                    {/* TOC note */}
                    {(k.toc_note || "").trim() && (
                      <div>
                        <p className="uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">TOC note</p>
                        <p className="mt-1 whitespace-pre-wrap text-[color:var(--text-muted)]">{k.toc_note}</p>
                      </div>
                    )}

                    {/* Introduction */}
                    {(k.introduction || "").trim() && (
                      <div>
                        <p className="uppercase tracking-widest text-[10px] text-[color:var(--text-muted)]">Introduction</p>
                        <p className="mt-1 whitespace-pre-wrap text-[color:var(--text-muted)]">{k.introduction}</p>
                      </div>
                    )}

                    {bits.length === 0 && (
                      <p className="italic text-[color:var(--text-muted)]">This kit is empty — open the editor to add presets.</p>
                    )}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {editingKit && (
        <CreateKitModal
          onClose={() => setEditingKit(null)}
          onSubmit={async (payload) => {
            const ok = await onUpdateKit(editingKit.id, payload);
            if (ok) setEditingKit(null);
          }}
          frontPresets={front} backPresets={back} infoPresets={info}
          editingKit={editingKit} team={team} workspaces={workspaces}
          currentTocNote={editingKit.toc_note || ""}
          currentIntroduction={editingKit.introduction || ""}
        />
      )}

      {/* iter-102 — bulk share modal */}
      {bulkOpen && (
        <BulkShareModal
          count={selectedIds.size}
          team={team}
          workspaces={workspaces}
          busy={bulkBusy}
          onClose={() => setBulkOpen(false)}
          onApply={async ({ teamOn, workspaceIds, mergeMode }) => {
            setBulkBusy(true);
            const ids = Array.from(selectedIds);
            const patches = ids.map(async (kitId) => {
              // Pick the current kit so we can MERGE workspace_ids
              // when the author opts to "add" rather than "replace".
              const current = kits.find((k) => k.id === kitId);
              let ws = Array.from(workspaceIds);
              if (mergeMode === "add" && current?.workspace_ids?.length) {
                const merged = new Set(current.workspace_ids);
                ws.forEach((w) => merged.add(w));
                ws = Array.from(merged);
              }
              const payload = {
                team_id: teamOn ? (team?.id || null) :
                  (mergeMode === "add" ? current?.team_id ?? null : null),
                workspace_ids: ws,
              };
              try {
                const { data } = await api.patch(`/preset-kits/${kitId}`, payload);
                return { ok: true, data };
              } catch (err) {
                return { ok: false, err };
              }
            });
            const results = await Promise.all(patches);
            const ok = results.filter((r) => r.ok).length;
            const failed = results.length - ok;
            if (failed === 0) {
              toast.success(`Updated ${ok} kit${ok === 1 ? "" : "s"}`);
            } else {
              toast.error(`${ok} updated, ${failed} failed`);
            }
            setBulkBusy(false);
            setBulkOpen(false);
            clearSelection();
            if (typeof onKitsChanged === "function") onKitsChanged();
          }}
        />
      )}
    </section>
  );
}

// -----------------------------------------------------------------------------
// BulkShareModal — small, focused modal for iter-102 "Share selected with…".
// -----------------------------------------------------------------------------
function BulkShareModal({ count, team, workspaces, busy, onClose, onApply }) {
  const [teamOn, setTeamOn] = useState(false);
  const [workspaceIds, setWorkspaceIds] = useState(() => new Set());
  // "replace" (default) wipes each kit's current scope with the new
  // pick; "add" merges — every selected kit keeps its current
  // workspaces AND gains the newly-ticked ones. "Add" is the safer
  // default when you're bulk-widening reach (e.g. "also share these
  // 3 kits with the new Studio C workspace").
  const [mergeMode, setMergeMode] = useState("add");
  const toggleWs = (wid) => {
    setWorkspaceIds((prev) => {
      const next = new Set(prev);
      if (next.has(wid)) next.delete(wid); else next.add(wid);
      return next;
    });
  };
  const anyTicked = teamOn || workspaceIds.size > 0;
  return (
    <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4"
         data-testid="pt-kits-bulk-modal"
         onClick={busy ? undefined : onClose}>
      <div className="bg-white max-w-md w-full max-h-[90vh] overflow-auto border border-[color:var(--border)]"
           onClick={(e)=>e.stopPropagation()}>
        <div className="flex items-center justify-between border-b border-[color:var(--border)] px-4 py-3">
          <p className="font-editorial text-lg">Share {count} kit{count === 1 ? "" : "s"} with…</p>
          <button onClick={onClose} disabled={busy}
                  className="text-[color:var(--text-muted)] hover:text-black p-1"
                  data-testid="pt-kits-bulk-modal-x">
            <X className="w-4 h-4"/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <div className="border border-[color:var(--border)] bg-[color:var(--canvas)]/50 p-3">
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-2">Mode</p>
            <label className="flex items-start gap-2 cursor-pointer text-xs">
              <input type="radio" name="mode" checked={mergeMode === "add"}
                     onChange={()=>setMergeMode("add")}
                     data-testid="pt-kits-bulk-mode-add"/>
              <div>
                <p className="font-medium">Add to existing scope</p>
                <p className="text-[10px] text-[color:var(--text-muted)]">Each kit keeps every workspace it already has AND gains the newly-ticked ones. Safest choice — never revokes existing access.</p>
              </div>
            </label>
            <label className="flex items-start gap-2 cursor-pointer text-xs mt-2">
              <input type="radio" name="mode" checked={mergeMode === "replace"}
                     onChange={()=>setMergeMode("replace")}
                     data-testid="pt-kits-bulk-mode-replace"/>
              <div>
                <p className="font-medium">Replace scope entirely</p>
                <p className="text-[10px] text-[color:var(--text-muted)]">Overwrite every selected kit's current sharing with the exact team / workspaces ticked below. Untouched controls become "unshared".</p>
              </div>
            </label>
          </div>
          {team && (
            <label className="flex items-start gap-2 cursor-pointer border border-[color:var(--border)] bg-[color:var(--canvas)]/50 p-3">
              <input type="checkbox" checked={teamOn} onChange={(e)=>setTeamOn(e.target.checked)}
                     data-testid="pt-kits-bulk-team"/>
              <div className="min-w-0">
                <p className="text-xs font-medium">Team · {team.name}</p>
              </div>
            </label>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-[color:var(--text-muted)] mb-1">Workspaces</p>
            {(workspaces || []).length === 0 ? (
              <p className="text-[10px] text-[color:var(--text-muted)]">
                You aren't in any workspace yet.
              </p>
            ) : (
              <div className="max-h-52 overflow-auto border border-[color:var(--border)] divide-y divide-[color:var(--border)]"
                   data-testid="pt-kits-bulk-workspaces">
                {workspaces.map((w) => (
                  <label key={w.id}
                         className="flex items-center gap-2 px-2 py-1.5 cursor-pointer hover:bg-[color:var(--canvas)]"
                         data-testid={`pt-kits-bulk-ws-${w.id}`}>
                    <input type="checkbox" checked={workspaceIds.has(w.id)}
                           onChange={()=>toggleWs(w.id)}
                           data-testid={`pt-kits-bulk-ws-${w.id}-cb`}/>
                    <span className="text-xs truncate">{w.name || "Workspace"}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-[color:var(--border)] px-4 py-3">
          <button onClick={onClose} disabled={busy}
                  className="btn-ghost text-xs" data-testid="pt-kits-bulk-cancel">
            Cancel
          </button>
          <button onClick={()=>onApply({ teamOn, workspaceIds, mergeMode })}
                  disabled={busy || (mergeMode === "replace" && !anyTicked && workspaceIds.size === 0)}
                  className="btn-primary text-xs inline-flex items-center gap-1"
                  data-testid="pt-kits-bulk-apply">
            {busy ? "Applying…" : `Apply to ${count} kit${count === 1 ? "" : "s"}`}
          </button>
        </div>
      </div>
    </div>
  );
}

export default KitsManager;
